import * as _ from 'lodash';
import { textStyle } from 'cornerstone-tools';
import writeStep from 'app/CornerstoneTools/utils/toolStepsExplanations/writeStep';
import wrapTextToWidth from 'app/utils/canvas/wrapTextToWidth';
import { BaseAnnotation } from 'cornerstone-tools';
import { Point } from 'app/interfaces/Image';

interface DrawToolStepExplanationsOptions {
  fontSize?: number;
  interline?: number;
  padding?: number;
  maxWidth?: number;
}

type GetMessageFunction = () => string;
type HandlesBySteps = string[][] | string[];

/**
 * Draw tool step explanations on canvas
 */
const drawToolStepExplanations = (
  ctx: CanvasRenderingContext2D,
  toolData: BaseAnnotation[] | undefined,
  isToolActive: boolean,
  getMessagesBySteps: GetMessageFunction[],
  handlesBySteps: HandlesBySteps = [],
  origin: Point = { x: 10, y: 20 },
  {
    fontSize = textStyle.getFontSize(),
    interline = 5,
    padding = 5,
    maxWidth = undefined,
  }: DrawToolStepExplanationsOptions = {},
  defaultStepIndex?: number
): void => {
  if (!isToolActive) return;

  let activeStepIndex = defaultStepIndex ?? 0;
  if (toolData?.length && _.every(toolData, { completed: true })) {
    if (defaultStepIndex === undefined) {
      return;
    }
  }

  if (toolData?.length && toolData.length > 0) {
    const movingHandle = _.findKey(toolData[0].handles, { moving: true });
    if (movingHandle) {
      activeStepIndex = _.findIndex(handlesBySteps, (handles: string | string[]) => {
        if (typeof handles === 'string') {
          return handles === movingHandle;
        }
        if (Array.isArray(handles)) {
          return handles.includes(movingHandle);
        }
        return false;
      });
    }
  }

  ctx.save();
  ctx.font = `${fontSize}px sans-serif`;

  let maxTextWidth = 0;
  const stepsLines: string[][] = [];

  getMessagesBySteps.forEach((getMessage) => {
    const stepLines = wrapTextToWidth(ctx, getMessage(), maxWidth);
    stepsLines.push(stepLines);
    maxTextWidth = Math.max(maxTextWidth, ...stepLines.map((line) => ctx.measureText(line).width));
  });

  ctx.fillStyle = 'black';
  ctx.globalAlpha = 0.5;
  const numberOfLines = _.sum(stepsLines.map((lines) => lines.length));
  ctx.fillRect(
    origin.x,
    origin.y,
    maxTextWidth + 2 * padding,
    2 * padding + numberOfLines * fontSize + (numberOfLines - 1) * interline
  );

  ctx.globalAlpha = 1;

  let yOffset = padding;
  stepsLines.forEach((stepLines, index) => {
    stepLines.forEach((line) => {
      writeStep(ctx, line, origin.x + padding, origin.y + yOffset, index === activeStepIndex);
      yOffset += fontSize + interline;
    });
  });

  ctx.restore();
};

export { drawToolStepExplanations };
